import {
  Box,
  CardActions,
  CardContent,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import React from "react";
import { EnquiryLabels } from "../../../schemes/enquiries/enquiries_list";
import { useNavigate } from "react-router-dom";
import {
  BlockRounded,
  CheckCircleRounded,
  ChevronRightRounded,
  DeleteOutlined,
} from "@mui/icons-material";
import { AppUsersList } from "../../model/app_users_list_model";
import useUser from "../../../../providers/user/user_provider";
import { black, orange, purple } from "../../../../theme/colors";
import {
  TableCell,
  TableEmptyState,
  TableFooterPagination,
  TableHeaderRow,
  TableRow,
} from "../table_toolkit/table_toolkit";

interface DataGridInterface {
  labels: EnquiryLabels[];
  data: AppUsersList[];
  count: number;
  page: number;
  setPage: (val: number) => void;
  pageSize: number;
  setPageSize: (val: number) => void;
  setBlockAppUserDialog: (val: boolean) => void;
  blockAppUserDialog: boolean;
  setSelectedUser: (val: AppUsersList) => void;
  setDeletePopUp?: (val: boolean) => void;
  genderList?: { id: number; value: string }[];
}

const ROWS_PER_PAGE_OPTIONS = [8, 25, 50, 100];

export default function DatGridUsers({
  labels,
  data,
  page,
  setPage,
  pageSize,
  setPageSize,
  count,
  setBlockAppUserDialog,
  blockAppUserDialog,
  setSelectedUser,
  setDeletePopUp,
  genderList,
}: DataGridInterface) {
  const { hasPermission } = useUser();
  const navigate = useNavigate();

  const genderById = new Map((genderList || []).map((g) => [g.id, g.value]));

  const startRange = (page - 1) * pageSize + 1;
  const endRange = Math.min(page * pageSize, count);

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage + 1);
  };

  const customText = `Showing ${startRange} to ${endRange} of ${count} entries`;

  function cardCell({
    label,
    text,
    flex,
  }: {
    label?: string;
    text?: string | null;
    flex?: number;
  }) {
    return (
      <TableCell label={label} flex={flex} bordered>
        <Typography
          fontSize={13.5}
          color={black}
          noWrap
          sx={{ textOverflow: "ellipsis" }}
        >
          {text ? text : "-"}
        </Typography>
      </TableCell>
    );
  }

  return (
    <Box display="flex" flex={1} flexDirection={"column"} width={"100%"}>
      <CardContent
        sx={{
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          p: 0,
          overflowX: "auto",
        }}
      >
        <Box sx={{ minWidth: { xs: 0, sm: 1180 } }}>
        <TableHeaderRow labels={labels} lastColumnWidth={140} bordered />
        {data?.length === 0 ? (
          <TableEmptyState message="No users found" />
        ) : (
          <Box display="flex" flexDirection="column">
            {data.map((item, index) => (
              <TableRow
                key={item?.id ?? index}
                onClick={() => navigate(`/users/userDetails/${item.id}`)}
                bordered
              >
                {cardCell({ label: "User id", text: item?.id?.toString() })}
                {cardCell({ label: "Full Name", text: item?.name?.toString(), flex: 1.8 })}
                {cardCell({ label: "Contact", text: item?.mobileNumber })}
                {cardCell({ label: "DOB", text: item?.dob })}
                {cardCell({ label: "Gender", text: genderById.get(item?.gender_id!) })}
                {cardCell({ label: "Marital Status", text: item?.maritalStatus })}
                {cardCell({ label: "State", text: item?.state })}
                {cardCell({ label: "District", text: item?.district })}
                {cardCell({ label: "Mode of Reg.", text: item?.registered_source })}
                {cardCell({ label: "Phone Type", text: item?.phone_type })}

                <TableCell isLast lastColumnWidth={140} bordered>
                <Box
                  display="flex"
                  alignItems={"center"}
                  justifyContent={{ xs: "flex-start", sm: "flex-end" }}
                  gap={0.5}
                >
                  <Tooltip title={item?.isBlocked ? "Unblock user" : "Block user"}>
                    <span>
                      <IconButton
                        disabled={!hasPermission("users", "update")}
                        onClick={(e) => {
                          e.stopPropagation();
                          setBlockAppUserDialog(!blockAppUserDialog);
                          setSelectedUser(item);
                        }}
                        sx={{
                          bgcolor: item?.isBlocked ? "#E7F5EC" : "#FAEDEA",
                          "&:hover": {
                            bgcolor: item?.isBlocked ? "#D6EEE0" : "#F5DCD4",
                          },
                        }}
                      >
                        {item?.isBlocked ? (
                          <CheckCircleRounded sx={{ color: "#1E6446" }} fontSize="small" />
                        ) : (
                          <BlockRounded sx={{ color: orange }} fontSize="small" />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Delete user">
                    <span>
                      <IconButton
                        disabled={!hasPermission("users", "delete")}
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeletePopUp?.(true);
                          setSelectedUser(item);
                        }}
                        sx={{
                          opacity: hasPermission("users", "delete") ? 1 : 0.5,
                          "&:hover": { bgcolor: "#FDEDEA" },
                        }}
                      >
                        <DeleteOutlined color={"primary"} fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <ChevronRightRounded sx={{ color: purple }} />
                </Box>
                </TableCell>
              </TableRow>
            ))}
          </Box>
        )}
        </Box>
      </CardContent>
      <CardActions sx={{ px: 2, py: 2 }}>
        <TableFooterPagination
          count={count}
          page={page - 1}
          onPageChange={handleChangePage}
          summaryText={customText}
          rowsPerPage={pageSize}
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          onRowsPerPageChange={setPageSize}
        />
      </CardActions>
    </Box>
  );
}
